import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Screen } from '../../app/Frame';
import { useMeContext, useWritable } from '../../app/MeContext';
import { useScreen, useSheet, useToast } from '../../app/shell-context';
import { formatDay, formatDayWithWeekday, formatTime } from '../../lib/time';
import { formatKop } from '../../lib/money';
import { plural, pluralWithCount } from '../../lib/plural';
import { reminderCopy } from '../../lib/reminders';
import { Avatar, Notice, Pill, Row } from '../../ui/parts';
import { BellIcon, ClockIcon, PinIcon } from '../../ui/icons';
import type { Appointment, Child } from '../../api/types';
import { useBooking } from '../BookingContext';
import { useCancelAppointment, useHistory } from '../api';
import { CancelSheet } from '../CancelSheet';

/** Главный экран клиента: ближайший приём, команда ребёнка и история. */
export function Home() {
  const me = useMeContext();
  const navigate = useNavigate();
  const { reset } = useBooking();
  const writable = useWritable();
  const children = me.children ?? [];
  const [childId, setChildId] = useState<number | null>(children[0]?.id ?? null);
  const child = children.find((c) => c.id === childId) ?? children[0] ?? null;

  useScreen(
    'Запись',
    writable && child
      ? {
          label: 'Записаться',
          onClick: () => {
            reset({ flow: 'new', childId: child.id });
            navigate('/pick');
          },
        }
      : null,
  );

  const history = useHistory();
  const tz = me.center.tz;
  const tf = me.center.timeFormat;
  const upcoming = (me.upcoming ?? []).filter((a) => !child || a.child_id === child.id);

  if (!child) return <NoChildren />;

  return (
    <Screen>
      {children.length > 1 ? (
        <div className="chips">
          {children.map((c) => (
            <button key={c.id} className="chip" aria-pressed={c.id === child.id} onClick={() => setChildId(c.id)}>
              {c.name}
            </button>
          ))}
        </div>
      ) : null}

      <div className="hero">
        <Avatar name={child.name} />
        <div className="hero-txt">
          <h2>{child.name}</h2>
          <p>{childSubtitle(child)}</p>
        </div>
      </div>

      <p className="eyebrow">Ближайшая запись</p>
      {upcoming.length ? (
        <NextAppointment appointment={upcoming[0]} />
      ) : (
        <Notice kind="calm">
          {writable
            ? 'Ближайших записей нет. Выберите специалиста ниже или нажмите «Записаться».'
            : 'Ближайших записей нет. Пока запись приостановлена, новую назначит администратор.'}
        </Notice>
      )}

      <p className="eyebrow">С кем занимается</p>
      <div className="rows">
        {child.team.length ? (
          child.team.map((t) => (
            <Row
              key={t.specialist_id}
              avatar={<Avatar name={t.specialist_name} photo={t.specialist_photo} small />}
              title={t.specialist_name}
              note={t.direction_title}
              disabled={!writable}
              onClick={() => {
                reset({
                  flow: 'new',
                  childId: child.id,
                  specialistId: t.specialist_id,
                  specialistName: t.specialist_name,
                });
                navigate('/service');
              }}
            />
          ))
        ) : (
          <Notice kind="calm">
            Специалист пока не закреплён. Напишите администратору — он закрепит, и запись откроется здесь.
          </Notice>
        )}
      </div>

      {history.data?.length ? (
        <>
          <p className="eyebrow">История</p>
          <div className="hist">
            {history.data.map((a) => (
              <div className="hist-item" key={a.id}>
                <div>
                  <b>
                    {formatDay(a.starts_at_utc, tz)} · {formatTime(a.starts_at_utc, tz, tf)}
                  </b>
                  <span>
                    {a.service_title} · {a.specialist_name}
                  </span>
                </div>
                <Pill kind="done">{historyLabel(a)}</Pill>
              </div>
            ))}
          </div>
        </>
      ) : null}

      <p className="footnote">Нужен специалист другой категории — напишите администратору</p>
    </Screen>
  );
}

function NextAppointment({ appointment }: { appointment: Appointment }) {
  const me = useMeContext();
  const navigate = useNavigate();
  const sheet = useSheet();
  const toast = useToast();
  const { reset } = useBooking();
  const writable = useWritable();
  const cancel = useCancelAppointment();
  const tz = me.center.tz;
  const tf = me.center.timeFormat;

  // Родитель не должен остаться перед шторкой отменённой записи: закрываем её,
  // как только сервер ответил, и говорим, вернутся ли деньги.
  useEffect(() => {
    if (!cancel.isSuccess) return;
    sheet.close();
    toast(cancel.data.refunded ? 'Запись отменена, оплата вернётся' : 'Запись отменена');
    cancel.reset();
  }, [cancel.isSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

  const location = me.center.locations.find((l) => l.id === appointment.location_id);

  return (
    <div className="card">
      <div className="card-head">
        <div>
          <p className="card-when">
            {formatDayWithWeekday(appointment.starts_at_utc, tz)} · {formatTime(appointment.starts_at_utc, tz, tf)}
          </p>
          <p className="card-what">
            {appointment.service_title} · {appointment.specialist_name}
          </p>
        </div>
        {appointment.status === 'confirmed' ? <Pill kind="paid">Оплачено</Pill> : <Pill kind="await">Без оплаты</Pill>}
      </div>
      <ul className="meta">
        <li>
          <span className="ico"><PinIcon /></span>
          <span>{location ? location.address : 'Видеосвязь, ссылка придёт в чат'}</span>
        </li>
        {reminderCopy(me.center.reminders) ? (
          <li>
            <span className="ico"><ClockIcon /></span>
            <span>{reminderCopy(me.center.reminders)}</span>
          </li>
        ) : null}
        <li>
          <span className="ico"><BellIcon /></span>
          <span>{formatKop(appointment.price_kop)}</span>
        </li>
      </ul>
      {writable ? (
        <div className="row-actions">
          <button
            className="btn"
            onClick={() => {
              reset({
                flow: 'move',
                movingId: appointment.id,
                childId: appointment.child_id,
                specialistId: appointment.specialist_id,
                specialistName: appointment.specialist_name ?? '',
                serviceId: appointment.service_id,
                serviceTitle: appointment.service_title ?? '',
                priceKop: appointment.price_kop,
                durationMin: Math.round((appointment.ends_at_utc - appointment.starts_at_utc) / 60_000),
                isOnline: appointment.location_id === null,
              });
              navigate('/calendar');
            }}
          >
            Перенести
          </button>
          <button
            className="btn ghost-danger"
            onClick={() =>
              sheet.open(
                <CancelSheet
                  appointment={appointment}
                  onConfirm={() => cancel.mutate(appointment.id)}
                  onClose={() => sheet.close()}
                />,
              )
            }
          >
            Отменить
          </button>
        </div>
      ) : null}
    </div>
  );
}

function NoChildren() {
  const me = useMeContext();
  useScreen('Запись', null);
  return (
    <Screen>
      <p className="lead">В карточке семьи пока нет ни одного ребёнка.</p>
      <Notice kind="calm">
        Напишите администратору организации — {me.center.adminContact || me.center.phone} — он заведёт карточку,
        и запись откроется здесь.
      </Notice>
    </Screen>
  );
}

function childSubtitle(child: Child): string {
  const parts: string[] = [];
  if (child.birth_year) {
    const age = new Date().getUTCFullYear() - child.birth_year;
    parts.push(`${age} ${plural(age, 'год', 'года', 'лет')}`);
  }
  parts.push(
    child.team.length
      ? `ведут ${pluralWithCount(child.team.length, 'специалист', 'специалиста', 'специалистов')}`
      : 'специалист пока не закреплён',
  );
  return parts.join(' · ');
}

function historyLabel(a: Appointment): string {
  if (a.status === 'no_show') return 'Неявка';
  if (a.status === 'cancelled') return 'Отменён';
  return 'Проведён';
}
